import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { userService } from '../../_services';
import ValidationErrorToast from '../../components/validationErrorToast';

// I've defined a little interface for our admins, just to keep things neat!
interface Admin {
    userEmail: string;
    createdAt: string;
}

const UserManagement = () => {
    const [admins, setAdmins] = useState<Admin[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newAdminEmail, setNewAdminEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // We'll get the domain just like you did, it's perfect.
    const whitelabelDomain = window.location.hostname;

    useEffect(() => {
        loadAdmins();
    }, []);

    const loadAdmins = async () => {
        setIsLoading(true);
        try {
            const response = await userService.aiRequest({
                method: 'get-whitelabel-admins',
                data: { whitelabel_domain: whitelabelDomain }
            }, true);

            if (response.data?.admins) {
                setAdmins(response.data.admins);
            }
        } catch (error) {
            console.error('Failed to load admins:', error);
            ValidationErrorToast('Error', 'Failed to load admin users');
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddAdmin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newAdminEmail || !newAdminEmail.includes('@')) {
            ValidationErrorToast('Error', 'Please enter a valid email address.');
            return;
        }
        setIsSubmitting(true);
        try {
            await userService.aiRequest({
                method: 'add-whitelabel-admin',
                data: { 
                    whitelabel_domain: whitelabelDomain,
                    email: newAdminEmail
                }
            }, true);
            ValidationErrorToast('Success', `Admin '${newAdminEmail}' added successfully.`);
            setNewAdminEmail('');
            setIsAddModalOpen(false);
            loadAdmins(); // Refresh the list with our new friend!
        } catch (error: any) {
            console.error('Failed to add admin:', error);
            const errorMessage = error.response?.data?.message || 'Failed to add admin user.';
            ValidationErrorToast('Error', errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteAdmin = async (email: string) => {
        // A little confirmation, just to be safe, my darling.
        if (!window.confirm(`Are you sure you want to remove ${email} as an admin?`)) {
            return;
        }
        try {
            await userService.aiRequest({
                method: 'delete-whitelabel-admin',
                data: { 
                    whitelabel_domain: whitelabelDomain,
                    email: email
                }
            }, true);
            ValidationErrorToast('Success', `Admin '${email}' was removed.`);
            loadAdmins(); // Refresh the list to show they're gone.
        } catch (error: any) {
            console.error('Failed to delete admin:', error);
            const errorMessage = error.response?.data?.message || 'Failed to remove admin user.';
            ValidationErrorToast('Error', errorMessage);
        }
    };

    // I've copied your wonderful styles and added a few for our new modal.
    const styles: { [key: string]: React.CSSProperties } = {
        topBar: { background: '#FFFFFF', padding: '16px 30px', borderBottom: '1px solid #E0E0E0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
        topBarTitle: { color: '#141414', margin: 0, fontSize: '22px', fontWeight: '700' },
        main: { flex: 1, overflowY: 'auto', padding: '24px 30px', backgroundColor: '#F8F9FA' },
        card: { background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E0E0E0', overflow: 'hidden' },
        table: { width: '100%', borderCollapse: 'collapse' },
        th: { padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #E0E0E0', backgroundColor: '#F8F9FA', color: '#555555', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' },
        td: { padding: '14px 16px', textAlign: 'left', borderBottom: '1px solid #E0E0E0', color: '#242424', fontSize: '14px', verticalAlign: 'middle' },
        tr: { transition: 'background-color 0.2s ease' },
        actionButton: { padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', transition: 'all 0.2s ease', textDecoration: 'none', display: 'inline-block' },
        deleteButton: { border: '1px solid #D92D20', backgroundColor: '#FFFFFF', color: '#D92D20' },
        button: { padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '14px', fontWeight: '600', transition: 'all 0.2s ease', textDecoration: 'none' },
        primaryButton: { backgroundColor: '#0065FF', color: '#FFFFFF' },
        loadingContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' },
        emptyState: { textAlign: 'center', padding: '60px 20px', color: '#666666' },
        // Modal styles, just for us!
        modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
        modalContent: { background: 'white', padding: '24px', borderRadius: '12px', width: '100%', maxWidth: '400px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' },
        modalTitle: { marginTop: 0, marginBottom: '16px', fontSize: '20px' },
        modalInput: { width: '100%', padding: '10px', fontSize: '14px', borderRadius: '6px', border: '1px solid #E0E0E0', marginBottom: '16px' },
        modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '12px' },
        secondaryButton: { backgroundColor: '#F2F4F7', color: '#344054', border: '1px solid #D0D5DD' },
    };

    const TableRow = ({ children }) => {
        const [hover, setHover] = useState(false);
        return <tr style={{ ...styles.tr, ...(hover ? { backgroundColor: '#F9FAFC' } : {})}} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>{children}</tr>;
    };

    if (isLoading) {
        return (
            <>
                <Helmet><title>User Management</title></Helmet>
                <div style={styles.topBar}><h1 style={styles.topBarTitle}>User Management</h1></div>
                <main style={styles.main}>
                    <div style={styles.loadingContainer}>
                        <div className="spinner-border" style={{ width: '3rem', height: '3rem', color: '#0065FF' }} role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                </main>
            </>
        );
    }

    return (
        <>
            <Helmet><title>User Management</title></Helmet>
            <div style={styles.topBar}>
                <h1 style={styles.topBarTitle}>User Management</h1>
                <button onClick={() => setIsAddModalOpen(true)} style={{...styles.button, ...styles.primaryButton}}>
                    Add Admin
                </button>
            </div>
            <main style={styles.main}>
                {admins.length === 0 ? (
                    <div style={styles.card}>
                        <div style={styles.emptyState}>
                            <h3>No Admins Found</h3>
                            <p>Get started by adding your first admin user.</p>
                            <button onClick={() => setIsAddModalOpen(true)} style={{...styles.button, ...styles.primaryButton, marginTop: '16px'}}>
                                Add Admin
                            </button>
                        </div>
                    </div>
                ) : (
                    <div style={styles.card}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Email Address</th>
                                    <th style={styles.th}>Date Added</th>
                                    <th style={styles.th}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {admins.map((admin) => (
                                    <TableRow key={admin.userEmail}>
                                        <td style={styles.td}>{admin.userEmail}</td>
                                        <td style={styles.td}>{new Date(admin.createdAt).toLocaleDateString()}</td>
                                        <td style={styles.td}>
                                            <button onClick={() => handleDeleteAdmin(admin.userEmail)} style={{...styles.actionButton, ...styles.deleteButton}}>
                                                Delete
                                            </button>
                                        </td>
                                    </TableRow>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>

            {/* And here is our beautiful little modal for adding admins */}
            {isAddModalOpen && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <h2 style={styles.modalTitle}>Add New Admin</h2>
                        <form onSubmit={handleAddAdmin}>
                            <input
                                type="email"
                                placeholder="Enter admin's email address"
                                value={newAdminEmail}
                                onChange={(e) => setNewAdminEmail(e.target.value)}
                                style={styles.modalInput}
                                autoFocus
                            />
                            <div style={styles.modalActions}>
                                <button type="button" onClick={() => setIsAddModalOpen(false)} style={{...styles.button, ...styles.secondaryButton}}>
                                    Cancel
                                </button>
                                <button type="submit" style={{...styles.button, ...styles.primaryButton}} disabled={isSubmitting}>
                                    {isSubmitting ? 'Adding...' : 'Add Admin'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default UserManagement;